import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { logoutOnServer, resolveSession } from './auth-api';
import {
  clearPendingCheckout,
  clearStaleCheckoutIntent,
  resumePendingCheckoutIfAny,
  savePendingCheckout,
  signupUrlWithReturn,
  startCheckout,
  userAlreadyHasPlan,
  wantsCheckoutResume,
} from './checkout';
import { portalUrls } from '../config';
import { clearSession, displayName, readStoredToken, readStoredUser } from './auth-storage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [token, setToken] = useState(() => readStoredToken());
  const [loading, setLoading] = useState(true);
  const [checkoutPending, setCheckoutPending] = useState(false);
  // Never auto-open overlay from leftover localStorage — only ?resume_checkout=1.
  const [resumingCheckout, setResumingCheckout] = useState(() => wantsCheckoutResume());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const session = await resolveSession();
      setToken(session.token);
      setUser(session.user);

      // Signup return (?resume_checkout=1): keep trying Stripe once auth is present.
      // Do NOT clear resume UI just because handoff is still settling.
      if (wantsCheckoutResume()) {
        setResumingCheckout(true);
        if (session.user) {
          try {
            const navigated = await resumePendingCheckoutIfAny(session.user);
            if (!navigated) {
              setResumingCheckout(false);
            }
          } catch (err) {
            console.error('Could not resume checkout after auth', err);
            setResumingCheckout(false);
          }
        }
      } else {
        clearStaleCheckoutIntent();
        setResumingCheckout(false);
      }
    } catch {
      clearSession();
      setToken(null);
      setUser(null);
      if (!wantsCheckoutResume()) {
        setResumingCheckout(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const beginCheckout = useCallback(async ({ plan = 'poa', interval = 'monthly', fallbackUrl } = {}) => {
    setCheckoutPending(true);
    try {
      const session = await resolveSession();
      setToken(session.token);
      setUser(session.user);

      if (session.user && session.token) {
        if (userAlreadyHasPlan(session.user, plan)) {
          clearPendingCheckout();
          window.location.assign(portalUrls.dashboard);
          return;
        }
        setResumingCheckout(true);
        await startCheckout({ plan, interval, fallbackUrl, user: session.user });
        return;
      }

      savePendingCheckout({ plan, interval, fallbackUrl });
      setResumingCheckout(true);
      window.location.href = signupUrlWithReturn();
    } catch (err) {
      setResumingCheckout(false);
      if (fallbackUrl) {
        window.location.assign(fallbackUrl);
        return;
      }
      throw err;
    } finally {
      setCheckoutPending(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => {
      if (!readStoredToken()) {
        refresh();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const logout = useCallback(async () => {
    const currentToken = readStoredToken();
    await logoutOnServer(currentToken);
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    checkoutPending,
    resumingCheckout,
    isAuthenticated: Boolean(user && (token || readStoredToken())),
    displayName: displayName(user),
    logout,
    refresh,
    beginCheckout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
